// AchievementDetailPage — Single achievement detail
// Layout: hero with badge art, progress, rewards, criteria

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  category: 'Trading' | 'Profit' | 'Challenge' | 'Social' | 'Bot';
  progress: number;
  target: number;
  unlocked: boolean;
  unlockedAt?: string;
  criteria: string[];
  reward: { amount?: number; tier?: 'Beginner' | 'Mid' | 'Pro'; title?: string };
}

const ACHIEVEMENT: Achievement = {
  id: 'a1',
  name: 'Profit Pioneer',
  description: 'Reach $5,000 in cumulative trading profit',
  emoji: '🏆',
  rarity: 'epic',
  category: 'Profit',
  progress: 2920,
  target: 5000,
  unlocked: false,
  criteria: [
    'Open and close at least 50 trades',
    'Maintain a win rate above 55%',
    'Never exceed 5% daily loss',
    'Cumulative profit reaches $5,000',
  ],
  reward: { amount: 100, title: 'Profit Pioneer badge' },
};

const RARITY_META = {
  common:    { color: '#8E8E93', label: 'Common' },
  rare:      { color: '#007AFF', label: 'Rare' },
  epic:      { color: '#AF52DE', label: 'Epic' },
  legendary: { color: '#FF9500', label: 'Legendary' },
};

export function AchievementDetailPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const a = ACHIEVEMENT;
  const rarity = RARITY_META[a.rarity];
  const pct = Math.min(100, (a.progress / a.target) * 100);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Achievement</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Hero — badge */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center" style={{ background: `linear-gradient(135deg, ${rarity.color} 0%, ${rarity.color}AA 100%)`, color: 'var(--tf-text-inverse)' }}>
          <div className="text-[11px] uppercase tracking-wider text-white/80">{a.category} · {rarity.label}</div>
          <div className="mt-3 mx-auto flex h-28 w-28 items-center justify-center rounded-[28px] text-[64px] bg-white/15" style={{ backdropFilter: 'blur(20px)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}>
            {a.emoji}
          </div>
          <div className="mt-3 text-[24px] font-bold tracking-tight">{a.name}</div>
          <p className="mt-1 text-[13px] max-w-xs mx-auto text-white/85">{a.description}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="mx-4 mt-3 rounded-[16px] border border-tf-border bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Progress</div>
          <div className="text-[12px] font-semibold tabular-nums" style={{ color: rarity.color }}>{pct.toFixed(0)}%</div>
        </div>
        <div className="mt-1 text-[24px] font-bold tabular-nums text-tf-text-1">${a.progress.toLocaleString()} <span className="text-[14px] text-tf-text-3 font-normal">/ ${a.target.toLocaleString()}</span></div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-tf-surface-3">
          <div className="h-full rounded-full transition-all" style={{ width: pct + '%', background: rarity.color }} />
        </div>
        <div className="mt-2 text-[11px] text-tf-text-3">${(a.target - a.progress).toLocaleString()} to go</div>
      </div>

      {/* Criteria */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">How to earn it</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {a.criteria.map((c, i, arr) => (
          <div key={i} className={['flex items-start gap-3 px-4 py-3', i < arr.length - 1 ? 'border-b border-tf-border' : ''].join(' ')} data-testid={`criterion-${i}`}>
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-tf-green-tint text-tf-profit text-[14px]">✓</div>
            <div className="text-[13px] text-tf-text-1">{c}</div>
          </div>
        ))}
      </div>

      {/* Reward */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Reward</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-green-tint text-tf-profit text-[18px]">💰</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-tf-text-1">${a.reward.amount} bonus</div>
            <div className="text-[11px] text-tf-text-3">Added to your wallet</div>
          </div>
        </div>
        <div className="flex items-center gap-3 border-t border-tf-border px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-purple-tint text-tf-info text-[18px]">🏅</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-tf-text-1">Profile badge</div>
            <div className="text-[11px] text-tf-text-3">{a.reward.title}</div>
          </div>
        </div>
      </div>

      {/* Rarity scale */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Rarity</h2>
      <div className="mx-4 grid grid-cols-4 gap-2">
        {(['common', 'rare', 'epic', 'legendary'] as const).map((r) => (
          <div key={r} className={['rounded-[12px] border p-2.5 text-center', r === a.rarity ? 'border-2' : 'border-tf-border'].join(' ')} style={r === a.rarity ? { borderColor: RARITY_META[r].color, backgroundColor: RARITY_META[r].color + '08' } : {}}>
            <div className="text-[10px] uppercase font-bold" style={{ color: RARITY_META[r].color }}>{RARITY_META[r].label}</div>
            <div className="text-[18px] mt-1">●</div>
            <div className="text-[10px] text-tf-text-3">{r === 'common' ? '1 in 10' : r === 'rare' ? '1 in 50' : r === 'epic' ? '1 in 200' : '1 in 1,000'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
